import { useSuspenseQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input, Label } from "~/components/ui";
import {
  GetEvidenceImporterDetailDocument,
  type GetEvidenceImporterDetailQuery,
  type GetEvidenceImporterDetailQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useEvidenceImporterForm } from "~/features/dataImportProjects/hooks/useEvidenceImporterForm";
import {
  EVIDENCE_IMPORTER_DEFINITION_KNOWN_KEYS,
  evidenceImporterDefinitionJsonSchema,
  zEvidenceImporterDefinition,
} from "~/features/dataImportProjects/utils/evidenceImporterDefinitionSchema";
import { useTenant } from "~/features/tenant/context";

import { DefinitionDiffModal } from "./DefinitionDiffModal";
import { DefinitionMonacoEditor } from "./DefinitionMonacoEditor";

const KNOWN_KEYS = new Set(EVIDENCE_IMPORTER_DEFINITION_KNOWN_KEYS);

type Props = {
  importProjectId: string;
  dataSourceId: string;
  /** `null` creates a new importer instead of editing an existing one. */
  evidenceImporterId: string | null;
  onSubmit: () => void;
};

export function EvidenceImporterEditForm({ importProjectId, dataSourceId, evidenceImporterId, onSubmit }: Props) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const [diffModalOpen, setDiffModalOpen] = useState(false);

  const { name, setName, resetByEvidenceImporter, submit, processing, jsonManager } = useEvidenceImporterForm({
    importProjectId,
    dataSourceId,
    evidenceImporterId,
    refetchQueriesOnEdit: evidenceImporterId
      ? [{ query: GetEvidenceImporterDetailDocument, variables: { tenantId, id: evidenceImporterId } }]
      : [],
    onSuccess: onSubmit,
  });

  const { data } = useSuspenseQuery<GetEvidenceImporterDetailQuery, GetEvidenceImporterDetailQueryVariables>(
    GetEvidenceImporterDetailDocument,
    { variables: { tenantId, id: evidenceImporterId ?? "" }, skip: !evidenceImporterId },
  );

  useEffect(() => {
    if (data) {
      resetByEvidenceImporter(data);
    }
  }, [data, resetByEvidenceImporter]);

  if (evidenceImporterId && !data?.evidenceImporters[0]) {
    throw new Error("Importer does not exist!");
  }

  const handleSave = () => {
    if (!name.trim()) {
      return;
    }
    setDiffModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="evidence-importer-name">{t("model.common.name")}</Label>
        <Input id="evidence-importer-name" value={name} onChange={(event) => setName(event.target.value)} required />
      </div>

      {jsonManager.hasChanges ? (
        <div className="flex justify-end border-b border-border-default pb-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (window.confirm(t("ui.discardAllChanges"))) {
                jsonManager.revertToRaw();
              }
            }}
          >
            {t("ui.revertToOriginal")}
          </Button>
        </div>
      ) : null}

      <DefinitionMonacoEditor
        value={jsonManager.editorJsonString}
        onChange={jsonManager.updateEditorString}
        jsonSchema={evidenceImporterDefinitionJsonSchema as Record<string, unknown>}
        schemaUri="evidence-importer-definition-schema"
      />

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={processing || !name.trim() || !jsonManager.isEditorJsonValid}
          onClick={handleSave}
        >
          {t("ui.save")}
        </Button>
      </div>

      <DefinitionDiffModal
        open={diffModalOpen}
        onClose={() => setDiffModalOpen(false)}
        onConfirm={() => {
          setDiffModalOpen(false);
          submit();
        }}
        rawJson={jsonManager.rawJson}
        workingJson={jsonManager.workingJson}
        zodSchema={zEvidenceImporterDefinition}
        knownKeys={KNOWN_KEYS}
      />
    </div>
  );
}
